import type { StatementProfileSave } from "@finances/shared";
import {
  AccountDeletable,
  AccountDeleted,
  AccountDeleteResult,
  AccountList,
  AiUsageSummary,
  AuditList,
  HouseholdDeleted,
  HouseholdLeft,
  MisfiledList,
  ConversationDetail,
  ConversationList,
  BudgetSuggestions,
  BudgetSummary,
  BudgetYearly,
  PlanList,
  PlanValue,
  CategoryList,
  CategoryMergeResult,
  CategorySuggestionList,
  TransactionBulkDeleteResult,
  TransactionBulkResult,
  DuplicateList,
  Insights,
  RulePreview,
  RuleReapplyResult,
  RuleWriteResult,
  StatementCommitResult,
  StatementProfileEvent,
  StatementProfileList,
  StatementUploadResult,
  StatementSniffResult,
  Dashboard,
  Household,
  InviteList,
  MemberList,
  MonthList,
  OnboardingProgress,
  PersonList,
  RuleList,
  StatementList,
  TransactionList,
  TransactionWipeResult,
  TransferMatchResult,
  TransferPairResult,
  type AccountCreate,
  type AccountPatch,
  type AuditQuery,
  type MisfiledQuery,
  type BudgetInput,
  type PlanValueInput,
  type CategoryCreate,
  type CategoryPatch,
  type ColumnMap,
  type DuplicateQuery,
  type CategorySuggestion,
  type RuleCreate,
  type RuleDraft,
  type RulePatch,
  type StatementFormat,
  type TransactionBulkPatch,
  type TransactionFilter,
  type TransactionPatch,
} from "@finances/shared";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { z } from "zod";
import { query, request, RequestFailed, send, type QueryParams } from "./api.js";
import { eventStream } from "./sse.js";
import type { Scope } from "./lastMonth.js";

/**
 * One hook per resource, and **every query key leads with the household id**.
 *
 * Switching household therefore invalidates everything by construction rather
 * than by remembering to clear a cache — the client-side mirror of the
 * server-side isolation rule. It is what prevents the alarming bug where one
 * household's numbers flash on screen under another's name.
 */

export const Me = z.object({
  user: z.object({ id: z.string(), email: z.string(), name: z.string() }),
  households: z.array(
    z.object({
      householdId: z.string(),
      name: z.string(),
      role: z.enum(["owner", "member", "viewer"]),
    }),
  ),
});
export type Me = z.infer<typeof Me>;

export const keys = {
  me: ["me"] as const,
  household: (h: string) => ["household", h] as const,
  dashboard: (h: string, scope: Scope, accountIds: string[] = []) =>
    [
      "household",
      h,
      "dashboard",
      scope.month ?? null,
      scope.period ?? null,
      accountIds.join(",") || null,
    ] as const,
  txns: (h: string, filter: Partial<TransactionFilter>) =>
    ["household", h, "txns", filter] as const,
  accounts: (h: string, month?: string, includeArchived = false) =>
    ["household", h, "accounts", month ?? null, includeArchived] as const,
  categories: (h: string, scope: Partial<Scope> = {}) =>
    ["household", h, "categories", scope.month ?? null, scope.period ?? null] as const,
  people: (h: string) => ["household", h, "people"] as const,
  rules: (h: string) => ["household", h, "rules"] as const,
  statements: (h: string) => ["household", h, "statements"] as const,
  statementProfiles: (h: string) => ["household", h, "statement-profiles"] as const,
  members: (h: string) => ["household", h, "members"] as const,
  invites: (h: string) => ["household", h, "invites"] as const,
  budgets: (h: string, month: string) => ["household", h, "budgets", month] as const,
  budgetSuggestions: (h: string, month: string, months: number) =>
    ["household", h, "budget-suggestions", month, months] as const,
  budgetYearly: (h: string, month: string) => ["household", h, "budget-yearly", month] as const,
  insights: (h: string, month: string) => ["household", h, "insights", month] as const,
  plan: (h: string) => ["household", h, "plan"] as const,
  conversations: (h: string) => ["household", h, "conversations"] as const,
  conversation: (h: string, id: string) => ["household", h, "conversations", id] as const,
  aiUsage: (h: string) => ["household", h, "ai-usage"] as const,
  /** Not household-scoped: it is about the person, and survives leaving one. */
  accountDeletable: ["account", "deletable"] as const,
  duplicates: (h: string, query: Partial<DuplicateQuery>) =>
    ["household", h, "duplicates", query] as const,
  audit: (h: string, query: Partial<AuditQuery>) => ["household", h, "audit", query] as const,
  misfiled: (h: string, query: Partial<MisfiledQuery>) =>
    ["household", h, "misfiled", query] as const,
  onboarding: (h: string) => ["household", h, "onboarding"] as const,
};

export function useMe(): UseQueryResult<Me> {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => request(Me, "/api/me"),
    // Signing in or out changes this and nothing else caches usefully.
    staleTime: 0,
    retry: false,
  });
}

/** `accountIds` empty is every account, and sends no parameter at all. */
export function useDashboard(household: string, scope: Scope, accountIds: string[] = []) {
  return useQuery({
    queryKey: keys.dashboard(household, scope, accountIds),
    queryFn: () =>
      request(
        Dashboard,
        `/api/dashboard${query({ month: scope.month, period: scope.period, accountIds })}`,
      ),
  });
}

export function useTransactions(household: string, filter: Partial<TransactionFilter>) {
  return useQuery({
    queryKey: keys.txns(household, filter),
    queryFn: () => request(TransactionList, `/api/transactions${query(filter as QueryParams)}`),
  });
}

/**
 * The list, a page at a time.
 *
 * Cursor pagination rather than an offset, because an import inserts rows
 * mid-scroll and an offset would show the same row twice or skip one. Loading
 * on demand also keeps the DOM bounded without a virtualiser: a phone renders
 * what someone asked to see, not a year.
 */
export function useTransactionPages(household: string, filter: Partial<TransactionFilter>) {
  return useInfiniteQuery({
    queryKey: keys.txns(household, filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      request(
        TransactionList,
        `/api/transactions${query({ ...(filter as QueryParams), cursor: pageParam })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/**
 * Every transaction filed under any category of kind `transfer` — not just
 * the one named "Transfers". The matcher and the totals both work this way
 * (`categoryKind()` reads the category's kind, never its name), so a screen
 * that assumed a single category would silently drop a household's second one.
 *
 * One query per category rather than a list on the wire: `categoryId` is
 * singular everywhere else this filter is used, and a household almost always
 * has exactly one of these, so this is one request in the common case.
 */
export function useTransferTransactions(household: string, categoryIds: string[]) {
  return useQueries({
    queries: categoryIds.map((categoryId) => ({
      queryKey: keys.txns(household, { categoryId, limit: 200 }),
      queryFn: () =>
        request(TransactionList, `/api/transactions${query({ categoryId, limit: 200 })}`),
    })),
  });
}

/**
 * What has been done to this household's ledger, newest first.
 *
 * Paged rather than capped: a history that silently stops at fifty entries is
 * one that cannot answer "when did this row change?" for anything older, which
 * is the only question worth opening it for.
 */
export function useAudit(household: string, filter: Partial<AuditQuery> = {}) {
  return useInfiniteQuery({
    queryKey: keys.audit(household, filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      request(AuditList, `/api/audit${query({ ...(filter as QueryParams), cursor: pageParam })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/**
 * Rows whose category contradicts something else in the ledger.
 *
 * Whole-ledger and unscoped by month, because a category filed wrongly in
 * March is just as wrong in August, and nobody would think to go looking for
 * it. Refetched sparingly for the same reason `useDuplicates` is: the answer
 * only moves when something is imported or recategorised.
 */
export function useMisfiled(household: string, filter: Partial<MisfiledQuery> = {}) {
  return useQuery({
    queryKey: keys.misfiled(household, filter),
    queryFn: () =>
      request(MisfiledList, `/api/transactions/miscategorised${query(filter as QueryParams)}`),
    staleTime: 60_000,
  });
}

/**
 * Rows that look like the same payment twice.
 *
 * The scan is a whole-ledger self-join, so it is the one list in the app worth
 * not refetching on every window focus — and the answer only changes when
 * something is imported or deleted, both of which invalidate the household.
 */
export function useDuplicates(household: string, filter: Partial<DuplicateQuery>) {
  return useQuery({
    queryKey: keys.duplicates(household, filter),
    queryFn: () =>
      request(DuplicateList, `/api/transactions/duplicates${query(filter as QueryParams)}`),
    staleTime: 60_000,
  });
}

/** Every month the household has any activity in, for the month pickers. */
export function useMonths(household: string) {
  return useQuery({
    queryKey: [...keys.household(household), "months"],
    queryFn: () => request(MonthList, "/api/transactions/months"),
    staleTime: 5 * 60_000,
  });
}

/**
 * Archived accounts are left out unless asked for, so the pickers that offer
 * an account to import into or file a transaction under cannot offer one the
 * household has put away.
 */
export function useAccounts(household: string, month?: string, includeArchived = false) {
  return useQuery({
    queryKey: keys.accounts(household, month, includeArchived),
    queryFn: () =>
      request(
        AccountList,
        `/api/accounts${query({ month, includeArchived: includeArchived || undefined })}`,
      ),
  });
}

export function useCategories(household: string, scope: Partial<Scope> = {}) {
  return useQuery({
    queryKey: keys.categories(household, scope),
    queryFn: () =>
      request(
        CategoryList,
        `/api/categories${query({ month: scope.month, period: scope.period })}`,
      ),
  });
}

export function usePeople(household: string) {
  return useQuery({
    queryKey: keys.people(household),
    queryFn: () => request(PersonList, "/api/people"),
  });
}

export function useRules(household: string) {
  return useQuery({
    queryKey: keys.rules(household),
    queryFn: () => request(RuleList, "/api/rules"),
  });
}

export function useStatements(household: string) {
  return useQuery({
    queryKey: keys.statements(household),
    queryFn: () => request(StatementList, "/api/statements"),
  });
}

export function useBudgets(household: string, month: string) {
  return useQuery({
    queryKey: keys.budgets(household, month),
    queryFn: () => request(BudgetSummary, `/api/budgets${query({ month })}`),
  });
}

/**
 * A suggested figure per category, computed from spending history — not a
 * model call (docs/08 §8.10). Deterministic for a given month, so it can
 * take the same longer stale time as insights.
 */
export function useBudgetSuggestions(household: string, month: string, months = 6) {
  return useQuery({
    queryKey: keys.budgetSuggestions(household, month, months),
    queryFn: () =>
      request(BudgetSuggestions, `/api/budgets/suggestions${query({ month, months })}`),
    staleTime: 5 * 60_000,
  });
}

/**
 * Budgeted categories judged on a trailing year rather than the one month
 * being viewed. Deterministic for a given month, same as the suggestions
 * it sits alongside, so it is safe to hold onto for a while.
 */
export function useBudgetYearly(household: string, month: string, enabled = true) {
  return useQuery({
    queryKey: keys.budgetYearly(household, month),
    queryFn: () => request(BudgetYearly, `/api/budgets/yearly${query({ month })}`),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/**
 * Insights are computed, not fetched from a model, so they are cheap and
 * identical on every request for the same month. A longer stale time than the
 * rest of the app is safe, and it keeps a page of a dozen findings from
 * refetching on every focus.
 */
/** Take-home per earner and the savings buffer: the typed-in half of the plan view. */
export function usePlan(household: string) {
  return useQuery({
    queryKey: keys.plan(household),
    queryFn: () => request(PlanList, "/api/plan"),
  });
}

export function useInsights(household: string, month: string) {
  return useQuery({
    queryKey: keys.insights(household, month),
    queryFn: () => request(Insights, `/api/insights${query({ month })}`),
    staleTime: 5 * 60_000,
  });
}

export function useConversations(household: string) {
  return useQuery({
    queryKey: keys.conversations(household),
    queryFn: () => request(ConversationList, "/api/conversations"),
  });
}

/**
 * A past exchange, replayed.
 *
 * `enabled` rather than a conditional hook: /ask is a screen with no
 * conversation open until one is, and a query that fetches "/api/conversations/"
 * would 404 for no reason.
 */
export function useConversation(household: string, id: string | undefined) {
  return useQuery({
    queryKey: keys.conversation(household, id ?? ""),
    queryFn: () => request(ConversationDetail, `/api/conversations/${id ?? ""}`),
    enabled: Boolean(id),
  });
}

export function useDeleteConversation(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => send(`/api/conversations/${id}`, { method: "DELETE" }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.conversations(household) }),
  });
}

/** What questions have cost. A product feature: hidden cost is a surprise. */
export function useAiUsage(household: string) {
  return useQuery({
    queryKey: keys.aiUsage(household),
    queryFn: () => request(AiUsageSummary, "/api/ai-usage"),
  });
}

export function useHousehold(household: string) {
  return useQuery({
    queryKey: keys.household(household),
    queryFn: () => request(Household, "/api/households/current"),
  });
}

export function useMembers(household: string) {
  return useQuery({
    queryKey: keys.members(household),
    queryFn: () => request(MemberList, "/api/households/current/members"),
  });
}

export function useInvites(household: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.invites(household),
    queryFn: () => request(InviteList, "/api/households/current/invites"),
    enabled,
  });
}

/**
 * Anything that changes money or how it is filed invalidates the whole
 * household prefix.
 *
 * Surgical invalidation is a false economy here: a recategorisation moves a
 * figure on the dashboard, in the category tree, in two lists and in the
 * uncategorised count, and the cost of refetching a household of two people's
 * data is a few kilobytes.
 */
function invalidateHousehold(client: QueryClient, household: string) {
  return client.invalidateQueries({ queryKey: ["household", household] });
}

export function useUpdateTransaction(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TransactionPatch }) =>
      send(`/api/transactions/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useBulkUpdateTransactions(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, patch }: TransactionBulkPatch) =>
      send("/api/transactions/bulk", { method: "POST", body: { ids, patch } }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Asking the model where the uncategorised rows belong.
 *
 * A mutation rather than a query, for the same reason previewing a rule is
 * one: it is an action someone takes deliberately, it costs the household
 * tokens, and asking twice must ask the server twice rather than replay a
 * cached answer. Nothing is written by it.
 */
export function useSuggestCategories() {
  return useMutation({
    mutationFn: (ids: string[]) =>
      request(CategorySuggestionList, "/api/transactions/suggest-categories", {
        method: "POST",
        body: { ids },
      }),
  });
}

/**
 * Committing the suggestions someone kept.
 *
 * Grouped by category and applied through the ordinary bulk endpoint — one
 * request per category, each audited as one entry, exactly as if the rows had
 * been ticked and filed by hand. The model's proposals get no shortcut into
 * the ledger, and what comes back is the server's count rather than ours.
 *
 * Sequential rather than parallel: these are writes to one table in one
 * household, and a failure halfway through is far easier to explain when the
 * requests that had already gone out are the ones before it.
 */
export function useApplySuggestions(household: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (suggestions: CategorySuggestion[]) => {
      const byCategory = new Map<string, string[]>();
      for (const suggestion of suggestions) {
        byCategory.set(suggestion.categoryId, [
          ...(byCategory.get(suggestion.categoryId) ?? []),
          suggestion.transactionId,
        ]);
      }

      let updated = 0;
      for (const [categoryId, ids] of byCategory) {
        const result = await request(TransactionBulkResult, "/api/transactions/bulk", {
          method: "POST",
          body: { ids, patch: { categoryId } },
        });
        updated += result.updated;
      }
      return updated;
    },
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * The only destructive action on a transaction. It is for a row an import
 * wrote twice; anything that genuinely happened is excluded, not deleted.
 */
export function useDeleteTransaction(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => send(`/api/transactions/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * The same delete, over the rows someone ticked on the duplicates screen.
 *
 * One request rather than a loop of `useDeleteTransaction`, so a batch either
 * lands or does not, and the list is invalidated once at the end instead of
 * refetching between every row.
 */
export function useDeleteTransactions(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      request(TransactionBulkDeleteResult, "/api/transactions/bulk-delete", {
        method: "POST",
        body: { ids },
      }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Auto-pairing every transfer leg it can, over a span of months — or the
 * whole ledger when no month is given. Safe to re-run: the matcher is
 * deterministic over the same rows, so a second call proposes the same pairs
 * and writes nothing new.
 */
export function useMatchTransfers(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (month?: string) =>
      request(TransferMatchResult, `/api/transactions/match-transfers${query({ month })}`, {
        method: "POST",
      }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Saying by hand that two rows are the two halves of one payment — for the
 * pairs the matcher cannot see because they did not settle for an exact,
 * same-week amount (uneven instalments funding a joint account, say).
 */
export function usePairTransactions(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ aId, bId }: { aId: string; bId: string }) =>
      request(TransferPairResult, "/api/transactions/pair", {
        method: "POST",
        body: { aId, bId },
      }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Breaking a pair. The row is still a transfer, it simply has no counterpart
 * again — back in the review queue rather than deleted or reclassified.
 */
export function useUnpairTransaction(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => send(`/api/transactions/${id}/pair`, { method: "DELETE" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Emptying the ledger. `confirm` is the household's name, and the server
 * checks it — the dialog is not the safeguard, it only asks.
 */
export function useWipeTransactions(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (confirm: string) =>
      request(TransactionWipeResult, "/api/transactions", {
        method: "DELETE",
        body: { confirm },
      }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Preview is a mutation rather than a query on purpose: it is an action
 * someone takes, it is not cached, and asking twice must ask the server twice
 * — the ledger may have moved between the two.
 */
export function usePreviewRule() {
  return useMutation({
    mutationFn: (draft: RuleDraft) =>
      request(RulePreview, "/api/rules/preview", { method: "POST", body: draft }),
  });
}

export function useReapplyRules(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (commit: boolean) =>
      request(RuleReapplyResult, "/api/rules/reapply", { method: "POST", body: { commit } }),
    onSuccess: (result) => {
      if (result.committed) return invalidateHousehold(client, household);
      return undefined;
    },
  });
}

/**
 * Upload archives the file and answers with a preview. Nothing is written yet.
 *
 * A PDF has a second outcome: a layout nobody has set up yet comes back as
 * `layout_unknown` with a token naming the stashed file, rather than as an
 * error. Nothing was imported, and nothing was sent anywhere.
 */
export function useUploadStatement() {
  return useMutation({
    mutationFn: ({ file, accountId }: { file: File; accountId: string }) => {
      const form = new FormData();
      form.set("file", file);
      form.set("accountId", accountId);
      return request(StatementUploadResult, "/api/statements", { method: "POST", body: form });
    },
  });
}

/**
 * A stateless look at a file for the column-mapping tool. Not cached — the
 * mapping tool calls it repeatedly as the user builds a mapping, and each
 * call is a fresh question, not a value worth remembering between them.
 */
export function useSniffStatement() {
  return useMutation({
    mutationFn: ({
      file,
      format,
      columnMap,
    }: {
      file: File;
      format?: StatementFormat;
      columnMap?: ColumnMap | null;
    }) => {
      const form = new FormData();
      form.set("file", file);
      if (format) form.set("format", format);
      if (columnMap) form.set("columnMap", JSON.stringify(columnMap));
      return request(StatementSniffResult, "/api/statements/sniff", { method: "POST", body: form });
    },
  });
}

export function useCommitStatement(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (statementId: string) =>
      request(StatementCommitResult, `/api/statements/${statementId}/commit`, { method: "POST" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useDeleteStatement(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (statementId: string) =>
      send(`/api/statements/${statementId}`, { method: "DELETE" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Saved PDF layouts.
 *
 * A layout belongs to the bank rather than to an account, so this is a
 * household-wide list — the same one reads every account held there, and next
 * month's statement too.
 */
export function useStatementProfiles(household: string) {
  return useQuery({
    queryKey: keys.statementProfiles(household),
    queryFn: () => request(StatementProfileList, "/api/statements/profiles"),
  });
}

/**
 * **The only call in this client that sends a statement to Claude**, and it is
 * wired to one button on one step of one modal. There is no retry that re-sends
 * without another press, and nothing else calls it.
 *
 * A stream rather than a request/response, because the call behind it takes a
 * minute and a minute of silence is at the mercy of every proxy between here
 * and the Worker. `onProgress` exists so the button can say which minute it is
 * on; a caller that does not care can leave it out and await the draft.
 */
export function useDeriveStatementProfile(
  onProgress?: (stage: DeriveStage) => void,
  /**
   * A `working` frame arrived — the model is still going.
   *
   * Separate from `onProgress` because it is not a stage: a heartbeat during a
   * retry must not move the label back to "reading". It says only that the
   * connection is alive, which after a minute of nothing is the thing a person
   * actually wants to know.
   */
  onAlive?: () => void,
) {
  return useMutation({
    mutationFn: async (token: string) => {
      const controller = new AbortController();

      for await (const event of eventStream(
        StatementProfileEvent,
        "/api/statements/profiles/derive",
        { token },
        controller.signal,
      )) {
        if (event.type === "started") onProgress?.("reading");
        else if (event.type === "working") onAlive?.();
        else if (event.type === "retrying") onProgress?.("retrying");
        else if (event.type === "draft") return event.draft;
        else if (event.type === "error") {
          // The stream is already a 200, so a failure arrives as a frame. It
          // becomes the same exception an ordinary failed request would throw,
          // so callers have one thing to catch.
          throw new RequestFailed(event.code, event.message, 200);
        }
      }

      throw new RequestFailed(
        "internal",
        "The connection ended before the layout came back. Try again.",
        200,
      );
    },
  });
}

/** What the derivation is doing, for the one line of copy that says so. */
export type DeriveStage = "reading" | "retrying";

/**
 * Answering "which of these accounts is this file about?".
 *
 * The answer names one of the ledgers the server found rather than carrying
 * rows, and the server re-reads and re-splits the archived file before using
 * it — so what is chosen is one of its own findings, not a payload.
 */
export function useChooseStatementLedger() {
  return useMutation({
    mutationFn: (choice: { token: string; accountId: string; ledgerIndex: number }) =>
      request(StatementUploadResult, "/api/statements/ledger", { method: "POST", body: choice }),
  });
}

/** Approving a layout also finishes the import it interrupted. */
export function useSaveStatementProfile(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: StatementProfileSave) =>
      request(StatementUploadResult, "/api/statements/profiles", {
        method: "POST",
        body,
      }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useRetireStatementProfile(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => send(`/api/statements/profiles/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/** The rows one statement upload added, for "what did this actually import". */
export function useStatementTransactions(household: string, statementId: string | null) {
  return useQuery({
    queryKey: keys.txns(household, { statementId: statementId ?? undefined, limit: 200 }),
    queryFn: () =>
      request(
        TransactionList,
        `/api/transactions${query({ statementId: statementId ?? undefined, limit: 200 })}`,
      ),
    enabled: Boolean(statementId),
  });
}

/**
 * Correcting an import filed under the wrong account. The whole household
 * prefix invalidates because the moved rows leave one account's totals and
 * land in another's.
 */
export function useReassignStatement(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ statementId, accountId }: { statementId: string; accountId: string }) =>
      send(`/api/statements/${statementId}/account`, { method: "PATCH", body: { accountId } }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useCreateRule(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: RuleCreate) => send("/api/rules", { method: "POST", body: input }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useUpdateRule(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: RulePatch }) =>
      send(`/api/rules/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/** Runs one existing rule over the ledger — the past half of an edit. */
export function useApplyRule(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request(RuleWriteResult, `/api/rules/${id}/apply`, { method: "POST" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useDeleteRule(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => send(`/api/rules/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useCreateCategory(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CategoryCreate) => send("/api/categories", { method: "POST", body: input }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useUpdateCategory(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: CategoryPatch }) =>
      send(`/api/categories/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Merging rewrites more of the app than anything else on that screen — the
 * ledger's names, the rules list, the budgets, every month's totals — so the
 * whole household prefix goes, as it does for a recategorisation.
 */
export function useMergeCategories(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ targetId, sourceIds }: { targetId: string; sourceIds: string[] }) =>
      request(CategoryMergeResult, `/api/categories/${targetId}/merge`, {
        method: "POST",
        body: { sourceIds },
      }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useDeleteCategory(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reassignTo }: { id: string; reassignTo?: string }) =>
      send(`/api/categories/${id}${query({ reassignTo })}`, { method: "DELETE" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Setting a budget is dated: the figure applies from `startsMonth` on, and the
 * months before it keep what they had. The whole household prefix is
 * invalidated because a budget moves a figure on this screen, on the
 * dashboard, and in any comparison drawn against it.
 */
export function useSetBudget(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, input }: { categoryId: string; input: BudgetInput }) =>
      request(BudgetSummary, `/api/budgets/${categoryId}`, { method: "PUT", body: input }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useEndBudget(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ categoryId, fromMonth }: { categoryId: string; fromMonth: string }) =>
      request(BudgetSummary, `/api/budgets/${categoryId}${query({ fromMonth })}`, {
        method: "DELETE",
      }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * Only the plan's own key goes stale: nothing else in the app reads these
 * figures, so invalidating the household would refetch every screen for no
 * change.
 */
export function useSetPlanValue(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, input }: { key: string; input: PlanValueInput }) =>
      request(PlanValue, `/api/plan/${key}`, { method: "PUT", body: input }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.plan(household) }),
  });
}

export function useRemovePlanValue(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => send(`/api/plan/${key}`, { method: "DELETE" }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.plan(household) }),
  });
}

export function useCreateAccount(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: AccountCreate) => send("/api/accounts", { method: "POST", body: input }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

export function useUpdateAccount(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: AccountPatch }) =>
      send(`/api/accounts/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * One hook for both directions: archiving and restoring are the same decision
 * taken twice, and a screen that offers one always has to offer the other.
 */
export function useArchiveAccount(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      send(`/api/accounts/${id}/${archived ? "archive" : "restore"}`, { method: "POST" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * The whole household prefix, because an account's transactions go with it:
 * the dashboard, every month's totals and the transaction list are all a row
 * or two lighter afterwards.
 */
export function useDeleteAccount(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request(AccountDeleteResult, `/api/accounts/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateHousehold(client, household),
  });
}

/**
 * The getting-started checklist.
 *
 * Every step is derived server-side from whether the household actually has an
 * account, a statement, a rule and a budget — so this refetches on the back of
 * `invalidateHousehold` like everything else, and a step ticks itself off the
 * moment the thing it asked for exists.
 */
export function useOnboarding(household: string) {
  return useQuery({
    queryKey: keys.onboarding(household),
    queryFn: () => request(OnboardingProgress, "/api/onboarding"),
  });
}

/**
 * Dismissing invalidates only the checklist. Putting it away changes nothing
 * about the household's money, and refetching the dashboard to find that out
 * is a round trip for no answer.
 */
export function useDismissOnboarding(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => send("/api/onboarding/dismiss", { method: "POST" }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.onboarding(household) }),
  });
}

export function useResumeOnboarding(household: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => send("/api/onboarding/resume", { method: "POST" }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.onboarding(household) }),
  });
}

/**
 * Leaving, deleting, and taking the data with you — docs/09 §9.7.
 *
 * All three invalidate `me` rather than the household, because all three
 * change which households exist for this person. `AuthGate` reads `me`, so
 * that one invalidation is also what moves them to the right screen: the
 * "create a household" state, or straight back to signed out.
 */
export function useAccountDeletable() {
  return useQuery({
    queryKey: keys.accountDeletable,
    queryFn: () => request(AccountDeletable, "/api/account/deletable"),
  });
}

export function useLeaveHousehold() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => request(HouseholdLeft, "/api/households/current/leave", { method: "POST" }),
    // Everything cached is about a household this person is no longer in.
    onSuccess: () => client.clear(),
  });
}

export function useDeleteHousehold() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (confirm: string) =>
      request(HouseholdDeleted, "/api/households/current", {
        method: "DELETE",
        body: { confirm },
      }),
    onSuccess: () => client.clear(),
  });
}

/** `useDeleteAccount` above is a *ledger* account. This one is the person. */
export function useDeleteMyAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (confirm: string) =>
      request(AccountDeleted, "/api/account", { method: "DELETE", body: { confirm } }),
    onSuccess: () => client.clear(),
  });
}

/**
 * The export is a plain link rather than a fetch, deliberately.
 *
 * Reading it into memory to make a blob URL would double the memory for a file
 * that can be tens of megabytes, and would lose the filename the server put in
 * `Content-Disposition`. A same-origin navigation sends the session cookie and
 * lets the browser stream it straight to disk.
 */
export function exportUrl(format: "json" | "csv"): string {
  return `/api/export?format=${format}`;
}
